import React, { useState } from 'react';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Box, Card, Skeleton, Typography, useTheme } from '@mui/material';

export interface DashboardKpiCardProps {
  title: string;
  value: number | string;
  icon: React.ReactElement;
  loading?: boolean;
  onClick?: () => void;
  active?: boolean;
  barLabel?: string;
  breakdown?: Array<{ label: string; value: number | string }>;
}

/** KPI card no padrão PA/PV (mesmo visual do dashboard). */
export function DashboardKpiCard({
  title,
  value,
  icon,
  loading,
  onClick,
  active = false,
  barLabel,
  breakdown = [],
}: DashboardKpiCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasBreakdown = breakdown.length > 0;
  const bottomLabel = barLabel ?? title;

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          height: 102,
          borderRadius: '10px',
          border: '1px solid #DEDEDE',
          backgroundColor: '#DDF3F8',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 1.25, pr: 1.25, pb: 3.25 }}>
          <Skeleton variant="circular" width={44} height={44} />
          <Skeleton variant="text" width="55%" sx={{ mt: 1, fontSize: 14 }} />
          <Skeleton variant="text" width="35%" sx={{ fontSize: 40 }} />
        </Box>
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 28, bgcolor: theme.palette.primary.main }} />
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: expanded && hasBreakdown ? 170 : 102,
        width: '100%',
        backgroundColor: '#DDF3F8',
        borderRadius: '10px',
        border: '1px solid #DEDEDE',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0px 3px 10px rgba(0,0,0,0.12)',
        ...(active ? { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 } : null),
      }}
    >
      <Box sx={{ p: 1.25, pb: 4.25, display: 'flex', gap: 1.25, alignItems: 'center' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
            color: theme.palette.primary.main,
            flex: '0 0 auto',
            '& svg': { width: 26, height: 26 },
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            pr: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              letterSpacing: '0px',
              color: '#5a5a5a',
              textTransform: 'uppercase',
              lineHeight: 1.1,
              textAlign: 'right',
            }}
            noWrap
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '40px',
              fontWeight: 700,
              letterSpacing: '0px',
              color: theme.palette.primary.main,
              lineHeight: 1,
            }}
            noWrap
          >
            {value}
          </Typography>
        </Box>
      </Box>

      {hasBreakdown && expanded && (
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: 68, px: 1.25, pb: 3.5 }}>
          <Box sx={{ px: 0.5 }}>
            {breakdown.map((row, idx) => (
              <Box key={`${row.label}-${idx}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, py: 0.25 }}>
                  <Typography sx={{ fontSize: '11px', color: '#707070', textTransform: 'uppercase', lineHeight: '16px' }} noWrap>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#707070', lineHeight: '16px' }} noWrap>
                    {row.value}
                  </Typography>
                </Box>
                {idx < breakdown.length - 1 && (
                  <Box sx={{ mx: 0.75, borderBottom: '1px dotted #bdbdbd', opacity: 0.9 }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 28,
          bgcolor: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          px: 1.25,
          cursor: hasBreakdown ? 'pointer' : 'default',
          boxShadow: '0px -2px 6px rgba(0,0,0,0.14)',
        }}
        onClick={(e) => {
          if (!hasBreakdown) return;
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0px',
            color: '#fff',
            textTransform: 'uppercase',
            flex: 1,
            minWidth: 0,
          }}
          noWrap
        >
          {bottomLabel}
        </Typography>
        {hasBreakdown && (
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            {expanded ? (
              <ArrowUpward sx={{ fontSize: 14, color: '#fff' }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 14, color: '#fff' }} />
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
}
